import { LocalizationPipe, LocalizationService, PermissionService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { finalize, map, of, switchMap } from 'rxjs';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { FeatureConstants } from 'src/core/constants/feature.constant';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { DisplayDatePipe } from 'src/core/pipes/display-date.pipe';
import {
  HolidayCalendarOptionsDto,
  HolidayCountryDto,
  ReferenceHolidayDto,
  TenantHolidayDto,
} from 'src/core/models/settings/holidays/holiday.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { FeatureService } from 'src/core/services/feature/feature.service';
import { HolidayCalendarService } from 'src/core/services/settings/holiday-calendar.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import {
  SupportedLocale,
  formatWeekday,
  getDatePlaceholder,
  getPrimeDateFormat,
  parseCalendarDate,
  resolveSupportedLocale,
  toCalendarDate,
} from 'src/core/utils/locale-date.util';

/** A reference calendar row plus the state the table needs. */
interface ReferenceHolidayRow extends ReferenceHolidayDto {
  weekday: string;
  selected: boolean;
}

/** A tenant calendar row plus its localized weekday. */
interface TenantHolidayRow extends TenantHolidayDto {
  weekday: string;
}

/** Value of the source filter above the tenant calendar: `all`, `custom`, or a country code. */
type SourceFilter = string;

const ALL_SOURCES: SourceFilter = 'all';
const CUSTOM_SOURCE: SourceFilter = 'custom';

/** Mirrors `HolidayConsts.MaxNameLength` on the server. */
const MAX_HOLIDAY_NAME_LENGTH = 128;

/** A year option shaped for `cx-select`, which expects objects. */
interface YearOption {
  value: number;
  label: string;
}

/**
 * Administration > Settings > Localization > Holidays.
 *
 * Left: the static country reference calendar, filtered by country and year, from which holidays are
 * picked in bulk. Right: what the tenant actually observes, including holidays it defined itself.
 * Both writes and removals hit the server immediately — there is no save step, and changing country or
 * year never touches the tenant calendar.
 */
@Component({
  selector: 'app-holiday-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    DatePickerModule,
    LocalizationPipe,
    DisplayDatePipe,
    CxSelectComponent,
  ],
  templateUrl: './holiday-settings.component.html',
  styleUrl: './holiday-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HolidaySettingsComponent implements OnInit {
  private readonly holidayCalendarService = inject(HolidayCalendarService);
  private readonly localization = inject(LocalizationService);
  private readonly permissionService = inject(PermissionService);
  private readonly featureService = inject(FeatureService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toaster = inject(ToasterService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly ALL_SOURCES = ALL_SOURCES;
  readonly CUSTOM_SOURCE = CUSTOM_SOURCE;
  readonly maxNameLength = MAX_HOLIDAY_NAME_LENGTH;

  // Holidays are whole calendar days, so the `displayDate` bindings in the template always pass the
  // `UTC` zone — that keeps `2026-01-01` rendering as the first of January in every time zone.

  /** Input mask of the custom holiday date picker, in PrimeNG's own pattern syntax. */
  primeDateFormat = getPrimeDateFormat(resolveSupportedLocale(this.localization.currentLang));
  datePlaceholder = getDatePlaceholder(resolveSupportedLocale(this.localization.currentLang));

  readonly isReferenceCalendarEnabled = this.featureService.isEnabled(
    FeatureConstants.holidayReferenceCalendar
  );

  readonly canEdit = this.permissionService.getGrantedPolicy(
    PermissionConstants.canManageHolidayCalendar
  );

  readonly customForm = new FormGroup({
    date: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(MAX_HOLIDAY_NAME_LENGTH)],
    }),
  });

  countries: HolidayCountryDto[] = [];
  yearOptions: YearOption[] = [];
  selectedCountryCode: string | null = null;
  selectedYear: number | null = null;

  referenceHolidays: ReferenceHolidayRow[] = [];
  addedHolidays: TenantHolidayRow[] = [];

  searchTerm = '';
  hideAdded = false;
  sourceFilter: SourceFilter = ALL_SOURCES;

  isLoadingOptions = true;
  isLoadingReference = false;
  isLoadingAdded = false;
  isSubmitting = false;

  private locale: SupportedLocale = resolveSupportedLocale(this.localization.currentLang);

  ngOnInit(): void {
    this.loadOptions();

    this.localization.languageChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(language => {
      this.locale = resolveSupportedLocale(language);
      this.primeDateFormat = getPrimeDateFormat(this.locale);
      this.datePlaceholder = getDatePlaceholder(this.locale);
      this.reload();
    });
  }

  get visibleReferenceHolidays(): ReferenceHolidayRow[] {
    const term = this.searchTerm.trim().toLocaleLowerCase(this.locale);

    return this.referenceHolidays.filter(holiday => {
      if (this.hideAdded && holiday.isAdded) {
        return false;
      }

      return !term || holiday.name.toLocaleLowerCase(this.locale).includes(term);
    });
  }

  get visibleAddedHolidays(): TenantHolidayRow[] {
    if (this.sourceFilter === ALL_SOURCES) {
      return this.addedHolidays;
    }

    if (this.sourceFilter === CUSTOM_SOURCE) {
      return this.addedHolidays.filter(holiday => holiday.isCustom);
    }

    return this.addedHolidays.filter(holiday => holiday.countryCode === this.sourceFilter);
  }

  get selectedKeys(): string[] {
    return this.referenceHolidays
      .filter(holiday => holiday.selected && !holiday.isAdded)
      .map(holiday => holiday.key);
  }

  get selectedCount(): number {
    return this.selectedKeys.length;
  }

  get addedReferenceCount(): number {
    return this.referenceHolidays.filter(holiday => holiday.isAdded).length;
  }

  get selectableVisibleHolidays(): ReferenceHolidayRow[] {
    return this.visibleReferenceHolidays.filter(holiday => !holiday.isAdded);
  }

  get isAllSelected(): boolean {
    const selectable = this.selectableVisibleHolidays;
    return selectable.length > 0 && selectable.every(holiday => holiday.selected);
  }

  get isSomeSelected(): boolean {
    const selectable = this.selectableVisibleHolidays;
    return selectable.some(holiday => holiday.selected) && !this.isAllSelected;
  }

  get canAddSelected(): boolean {
    return this.canEdit && !this.isSubmitting && this.selectedCount > 0;
  }

  get canAddCustom(): boolean {
    return this.canEdit && !this.isSubmitting && this.customForm.valid;
  }

  onCountryChange(countryCode: string | null): void {
    this.selectedCountryCode = countryCode;
    this.loadReferenceHolidays();
  }

  onYearChange(year: number | null): void {
    this.selectedYear = year;
    this.loadReferenceHolidays();
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
  }

  onHideAddedChange(hideAdded: boolean): void {
    this.hideAdded = hideAdded;
  }

  onSourceFilterChange(filter: SourceFilter): void {
    this.sourceFilter = filter;
  }

  onToggleHoliday(holiday: ReferenceHolidayRow): void {
    if (holiday.isAdded || !this.canEdit) {
      return;
    }

    holiday.selected = !holiday.selected;
  }

  onToggleAll(): void {
    if (!this.canEdit) {
      return;
    }

    const selectAll = !this.isAllSelected;
    this.selectableVisibleHolidays.forEach(holiday => (holiday.selected = selectAll));
  }

  onAddSelected(): void {
    const keys = this.selectedKeys;

    if (!this.canAddSelected || !this.selectedCountryCode || !this.selectedYear) {
      return;
    }

    this.isSubmitting = true;

    this.holidayCalendarService
      .addSelected({ countryCode: this.selectedCountryCode, year: this.selectedYear, keys })
      .pipe(
        finalize(() => this.releaseSubmitting()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          if (result.addedCount === 1) {
            this.toaster.success(
              this.localization.instant('Settings::Holidays:AddedSingle', result.added[0].name)
            );
          } else if (result.addedCount > 1) {
            this.toaster.success(
              this.localization.instant(
                'Settings::Holidays:AddedMultiple',
                String(result.addedCount)
              )
            );
          } else {
            this.toaster.info(this.localization.instant('Settings::Holidays:NoneAdded'));
          }

          this.reload();
        },
      });
  }

  onAddCustom(): void {
    if (!this.canAddCustom) {
      return;
    }

    const date = toCalendarDate(this.customForm.controls.date.value);
    const name = this.customForm.controls.name.value.trim();

    if (!date || !name) {
      return;
    }

    this.isSubmitting = true;

    this.holidayCalendarService
      .create({ date, name })
      .pipe(
        finalize(() => this.releaseSubmitting()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: holiday => {
          this.customForm.reset({ date: null, name: '' });
          this.toaster.success(
            this.localization.instant('Settings::Holidays:AddedSingle', holiday.name)
          );
          this.loadAddedHolidays();
        },
      });
  }

  onRemove(holiday: TenantHolidayRow): void {
    if (!this.canEdit) {
      return;
    }

    this.confirmationService
      .warn(
        this.localization.instant('Settings::Holidays:RemoveConfirmMessage', holiday.name),
        'Settings::Holidays:RemoveConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::Holidays:Remove',
          cancelText: 'AbpUi::Cancel',
        }
      )
      .pipe(
        switchMap(status =>
          status === Confirmation.Status.confirm
            ? this.holidayCalendarService.delete(holiday.id).pipe(map(() => true))
            : of(false)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toaster.success(
            this.localization.instant('Settings::Holidays:Removed', holiday.name)
          );
          this.reload();
        },
      });
  }

  trackByKey(_index: number, holiday: ReferenceHolidayRow): string {
    return holiday.key;
  }

  trackById(_index: number, holiday: TenantHolidayRow): number {
    return holiday.id;
  }

  private releaseSubmitting(): void {
    this.isSubmitting = false;
    this.cdr.markForCheck();
  }

  private loadOptions(): void {
    this.isLoadingOptions = true;

    this.holidayCalendarService
      .getOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: options => {
          this.applyOptions(options);
          this.isLoadingOptions = false;
          this.cdr.markForCheck();
          this.reload();
        },
        error: () => {
          this.isLoadingOptions = false;
          this.cdr.markForCheck();
        },
      });
  }

  private applyOptions(options: HolidayCalendarOptionsDto): void {
    this.countries = options.countries ?? [];
    this.yearOptions = (options.years ?? []).map(year => ({ value: year, label: String(year) }));

    const hasSelectedCountry = this.countries.some(
      country => country.code === this.selectedCountryCode
    );
    if (!hasSelectedCountry) {
      this.selectedCountryCode = options.defaultCountryCode || this.countries[0]?.code || null;
    }

    const hasSelectedYear = this.yearOptions.some(option => option.value === this.selectedYear);
    if (!hasSelectedYear) {
      this.selectedYear = options.defaultYear || this.yearOptions[0]?.value || null;
    }
  }

  private reload(): void {
    if (this.isReferenceCalendarEnabled) {
      this.loadReferenceHolidays();
    }

    this.loadAddedHolidays();
  }

  private loadReferenceHolidays(): void {
    if (!this.isReferenceCalendarEnabled || !this.selectedCountryCode || !this.selectedYear) {
      this.referenceHolidays = [];
      this.cdr.markForCheck();
      return;
    }

    this.isLoadingReference = true;

    this.holidayCalendarService
      .getAvailable(this.selectedCountryCode, this.selectedYear)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: holidays => {
          this.referenceHolidays = holidays.map(holiday => ({
            ...holiday,
            weekday: this.buildWeekday(holiday.date),
            selected: false,
          }));
          this.isLoadingReference = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.referenceHolidays = [];
          this.isLoadingReference = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadAddedHolidays(): void {
    this.isLoadingAdded = true;

    this.holidayCalendarService
      .getList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: holidays => {
          this.addedHolidays = holidays.map(holiday => ({
            ...holiday,
            weekday: this.buildWeekday(holiday.date),
          }));
          this.isLoadingAdded = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingAdded = false;
          this.cdr.markForCheck();
        },
      });
  }

  private buildWeekday(date: string): string {
    const parsed = parseCalendarDate(date);

    return parsed ? formatWeekday(parsed, this.locale) : '';
  }
}
